import React, { Fragment } from 'react';
import { AsYouType, parsePhoneNumber } from 'libphonenumber-js';
import HtmlInput from '../html/Input';
import InputError from '../errors/InputError';

const isModifierKey = event => {
  const key = event.keyCode;
  return (
    event.shiftKey === true ||
    key === 35 ||
    key === 36 || // Allow Shift, Home, End
    key === 9 ||
    key === 13 ||
    key === 46 ||
    key === 188 || // Allow Backspace (key=8), Tab, Enter, Delete, ','
    (key > 36 && key < 41) || // Allow left, up, right, down
    // Allow Ctrl/Command + A,C,V,X,Z
    ((event.ctrlKey === true || event.metaKey === true) &&
      (key === 65 || key === 67 || key === 86 || key === 88 || key === 90 || key === 91))
  );
};

export const processPhoneNumbersString = value => {
  if (!value || value === '+1' || value === '+1 ' || value.trim() === '') {
    return { plainNumbers: '', nationalNumbers: '' };
  }

  const inputPhoneNumberArray = value.split(',').map(number => {
    try {
      return parsePhoneNumber(number, 'US');
    } catch (e) {
      const cleanNumber = number.replace(/^\+1\s*/, '').trim();
      return {
        number,
        nationalNumber: cleanNumber,
        countryCallingCode: cleanNumber === '' ? '' : '1',
      };
    }
  });

  const validNumbers = inputPhoneNumberArray.filter((num, index) => {
    if (index === inputPhoneNumberArray.length - 1) {
      return true;
    }
    return num.nationalNumber && num.nationalNumber.trim() !== '';
  });

  if (validNumbers.length === 0) {
    return { plainNumbers: '', nationalNumbers: '' };
  }

  const plainNumbers = validNumbers
    .map(_ => `${_.countryCallingCode === '' ? '' : '+'}${_.countryCallingCode} ${_.nationalNumber}`)
    .join(',');
  const nationalNumbers = validNumbers.map(_ => (_.countryCallingCode === '1' ? _.nationalNumber : _.number)).join(',');
  return { plainNumbers, nationalNumbers };
};

export const formatPhoneNumber = data => {
  if (!data || data.trim() === '') {
    return '';
  }

  const inputPhoneNumberArray = data.split(',');
  let generatedOutput = '';

  for (let i = 0; i < inputPhoneNumberArray.length; i++) {
    const number = inputPhoneNumberArray[i].trim();
    if (number || i === inputPhoneNumberArray.length - 1) {
      if (generatedOutput && (number || i === inputPhoneNumberArray.length - 1)) {
        generatedOutput = generatedOutput.concat(', ');
      }
      if (number) {
        generatedOutput = generatedOutput.concat(new AsYouType('US').input(number));
      }
    }
  }

  return generatedOutput;
};

export const phoneNumberFormatPipeline = value => formatPhoneNumber(processPhoneNumbersString(value).nationalNumbers);

interface PhoneInputProps {
  value?: string;
  onChange: (value: string) => void;
  Input?: React.ComponentType<any>;
  Error?: React.ComponentType<any>;
  placeholder?: string;
  isHidden?: boolean;
  autoFocus?: boolean;
  isDisabled?: boolean;
  name?: string;
  error?: string;
  hasError?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

interface PhoneInputState {
  value: string;
  displayValue?: string;
}

export default class PhoneInput extends React.Component<PhoneInputProps, PhoneInputState> {
  private myRef: React.RefObject<any>;

  constructor(props: PhoneInputProps) {
    super(props);
    this.myRef = React.createRef();
    this.state = {
      value: '',
      displayValue: props.value ? phoneNumberFormatPipeline(props.value) : undefined,
    };
  }

  processEvent = e => processPhoneNumbersString(e.target.value);

  handleChange = e => {
    const { nationalNumbers, plainNumbers } = this.processEvent(e);
    this.setState({ displayValue: formatPhoneNumber(nationalNumbers) });
    this.props.onChange(plainNumbers);
  };

  handleKeyDown = e => {
    if (e.keyCode === 8 || e.keyCode === 46) {
      const value = e.target.value;
      const cursorPos = e.target.selectionStart;

      if (e.keyCode === 8 && cursorPos > 0) {
        const charBeforeCursor = value[cursorPos - 1];
        if ('() -'.includes(charBeforeCursor)) {
          e.preventDefault();
          const newPos = cursorPos - 1;
          e.target.setSelectionRange(newPos, newPos);

          const event = new KeyboardEvent('keydown', {
            key: 'Backspace',
            keyCode: 8,
            which: 8,
            bubbles: true,
            cancelable: true,
          });
          e.target.dispatchEvent(event);
        }
      }
      return;
    }
  };

  handleOnKeyUp = e => {
    if (e.target.selectionStart !== e.target.selectionEnd || isModifierKey(e)) {
      return;
    }

    const { nationalNumbers } = this.processEvent(e);
    const generatedOutput = formatPhoneNumber(e.target.value);

    if (e.keyCode !== 8 && e.keyCode !== 46) {
      const currentCursorPosition = e.target.selectionStart;
      let finalCursorPosition = currentCursorPosition;

      if (currentCursorPosition === e.target.value.length) {
        finalCursorPosition = generatedOutput.length;
      }

      e.target.selectionStart = finalCursorPosition;
      e.target.selectionEnd = finalCursorPosition;
    }
  };

  componentDidUpdate(prevProps: PhoneInputProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({
        displayValue: this.props.value ? phoneNumberFormatPipeline(this.props.value) : '',
      });
    }
  }

  render() {
    const {
      Input = HtmlInput,
      Error = InputError,
      placeholder = '',
      isHidden,
      autoFocus,
      value,
      isDisabled,
      name = 'text',
      error,
      hasError,
      onKeyDown, // do not delete because of ...rest
      onChange, // do not delete because of ...rest
      ...rest
    } = this.props;
    return (
      <Fragment>
        <Input
          ref={this.myRef}
          id={name}
          type={isHidden ? 'hidden' : 'text'}
          name={name}
          autoComplete="on"
          autoCapitalize="off"
          autoCorrect="off"
          placeholder={placeholder}
          autoFocus={autoFocus || false}
          value={this.state.displayValue || ''}
          onChange={this.handleChange}
          onKeyUp={this.handleOnKeyUp}
          onKeyDown={this.handleKeyDown}
          disabled={isDisabled}
          hasError={hasError}
          {...rest}
        />
        <Error isVisible={hasError} id={name}>
          {hasError ? error : ''}
        </Error>
      </Fragment>
    );
  }
}
